import { FC, useState } from "react";
import {
  Box,
  createStyles,
  makeStyles,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  ChevronRight as ChevronRightIcon,
  ReportProblemOutlined as ReportProblemOutlinedIcon,
} from "@material-ui/icons";

import { ListingTerm } from "components";
import { BuyerDashboardItem } from "containers/BuyerDashboard/assetAdministrationsPostProcessor";
import theme from "theme";

import { Column } from "./index";

const useStyles = makeStyles(() =>
  createStyles({
    tableRow: {
      backgroundColor: theme.palette.background.default,
    },
    tableRowHover: {
      backgroundColor: theme.custom.palette.gray.light,
      cursor: "pointer",
    },
    gapRow: {
      height: theme.spacing(2),
      borderBottom: `1px solid ${theme.custom.palette.gray.main}`,
    },
    arrowRightTitleCell: {
      borderBottom: "none",
    },
    titleTableCell: {
      borderBottom: "none",
      "& p": {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
    titleInfoRow: {
      padding: theme.spacing(1, 0),
    },
    titleInfoLeftContainer: {
      width: "100%",
      display: "flex",
      gap: `${theme.spacing(1)}px`,
      alignItems: "center",
    },
    dataTableCell: {
      textAlign: "center",
      position: "relative",
      "&::before": {
        content: "''",
        position: "absolute",
        top: "50%",
        height: "50%",
        right: "0px",
        width: "0.5px",
        transform: "translateY(-50%)",
        backgroundColor: theme.custom.palette.gray.dark,
      },
      "&:nth-last-child(2)::before": {
        display: "none",
      },
      "&::after": {
        content: "''",
        position: "absolute",
        top: "50%",
        height: "50%",
        left: "0px",
        width: "0.5px",
        transform: "translateY(-50%)",
        backgroundColor: theme.custom.palette.gray.dark,
      },
      "&:first-child::after": {
        display: "none",
      },
    },
    updatedText: {
      fontSize: "0.8rem",
      color: theme.custom.palette.gray.dark,
    },
    thinAlert: {
      width: "100%",
      padding: "0px 16px",
      fontSize: "0.8rem",
      alignItems: "center",
    },
    warning: {
      color: theme.custom.palette.yellow.main,
    },
  }),
);

interface AssetsTableRowProps {
  buyerDashboardItem: BuyerDashboardItem;
  responsiveColumns: Column[];
  titleColumn: Column;
  isLastRow: boolean;
  onDetailsClick: (buyerDashboardItem: BuyerDashboardItem) => void;
}

const AssetsTableRow: FC<AssetsTableRowProps> = ({
  buyerDashboardItem,
  titleColumn,
  responsiveColumns,
  isLastRow,
  onDetailsClick,
}) => {
  const classes = useStyles();

  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <>
      <TableRow
        className={`${classes.tableRow} ${isHovered && classes.tableRowHover}`}
        onClick={() => onDetailsClick(buyerDashboardItem)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <TableCell
          colSpan={responsiveColumns.length}
          className={classes.titleTableCell}
        >
          <Box display="flex">
            <Box minWidth="134px">
              <Box display="flex" alignItems="center">
                <Box position="relative" height="24px" width="24px">
                  {buyerDashboardItem.hasPaymentStatusUpdate ? (
                    <Tooltip title="Payment Status Update">
                      <ReportProblemOutlinedIcon
                        className={classes.warning}
                        style={{
                          position: "absolute",
                          right: "8px",
                          top: "16px",
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Box width="24px" />
                  )}
                </Box>
                <ListingTerm term={buyerDashboardItem.purchasedTerm} />
              </Box>
            </Box>
            <Typography>
              {titleColumn.getFormattedData(buyerDashboardItem)}
            </Typography>
          </Box>
        </TableCell>
        <TableCell className={classes.arrowRightTitleCell}>
          <Box style={{ position: "relative" }}>
            <ChevronRightIcon
              style={{
                position: "absolute",
                right: "0px",
                top: "4px",
                fontSize: "24px",
              }}
            />
          </Box>
        </TableCell>
      </TableRow>
      <TableRow
        className={`${classes.tableRow} ${isHovered && classes.tableRowHover}`}
        onClick={() => onDetailsClick(buyerDashboardItem)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {responsiveColumns.map(column => (
          <TableCell key={column.title} className={classes.dataTableCell}>
            {column.getFormattedData(buyerDashboardItem)}
          </TableCell>
        ))}
        <TableCell />
      </TableRow>
      {!isLastRow && <TableRow className={classes.gapRow} />}
    </>
  );
};

export default AssetsTableRow;
